import React, { useState, useRef, useEffect } from 'react';
import './DashboardBar.css';

interface DashboardBarProps {
  username: string;
  isAdmin?: boolean;
  onProfileClick: () => void;
  onActivityClick: () => void;
  onAdminClick?: () => void;
  onLogout: () => void;
}

const DashboardBar: React.FC<DashboardBarProps> = ({
  username,
  isAdmin,
  onProfileClick,
  onActivityClick,
  onAdminClick,
  onLogout
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleUsernameClick = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleProfileClick = () => {
    setIsDropdownOpen(false);
    onProfileClick();
  };

  const handleActivityClick = () => {
    setIsDropdownOpen(false);
    onActivityClick();
  };

  const handleAdminClick = () => {
    setIsDropdownOpen(false);
    onAdminClick?.();
  };

  const handleLogoutClick = () => {
    setIsDropdownOpen(false);
    onLogout();
  };

  const handleKeyDown = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  };

  return (
    <div className="dashboard-bar">
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <img src="/dc-web-logo.png" alt="Data Crate Logo" style={{ width: 48, height: 48, objectFit: 'contain' }} />
        <h1 className="dashboard-bar-title">Welcome!</h1>
      </div>

      <div style={{ textAlign: 'center' }}>
        <div style={{ fontFamily: "'Alike', serif", fontSize: '1.5rem', fontWeight: 800, lineHeight: 1.2 }}>
          <span style={{ color: '#667eea' }}>Data </span><span style={{ color: '#764ba2' }}>Crate</span>
        </div>
        <div style={{ fontFamily: "'Alike', serif", fontSize: '0.75rem', color: '#111', marginTop: '0.2rem', letterSpacing: '0.03em' }}>
          Smart. Simple. Secure. Storage.
        </div>
      </div>

      <div className="dashboard-bar-info">
        <div className="info-item username-dropdown" ref={dropdownRef}>
          <button
            className="username-trigger"
            onClick={handleUsernameClick}
            onKeyDown={(e) => handleKeyDown(e, handleUsernameClick)}
            aria-expanded={isDropdownOpen}
            aria-haspopup="true"
          >
            <span className="username-text">{username}</span>
            <span className="dropdown-arrow" aria-hidden="true">
              {isDropdownOpen ? '▲' : '▼'}
            </span>
          </button>

          {isDropdownOpen && (
            <div className="username-dropdown-menu" role="menu">
              <button
                className="dropdown-item"
                onClick={handleProfileClick}
                onKeyDown={(e) => handleKeyDown(e, handleProfileClick)}
                role="menuitem"
                tabIndex={0}
              >
                👤 My Profile
              </button>
              <button
                className="dropdown-item"
                onClick={handleActivityClick}
                onKeyDown={(e) => handleKeyDown(e, handleActivityClick)}
                role="menuitem"
                tabIndex={0}
              >
                📋 Activity
              </button>
              {isAdmin && (
                <button
                  className="dropdown-item"
                  onClick={handleAdminClick}
                  onKeyDown={(e) => handleKeyDown(e, handleAdminClick)}
                  role="menuitem"
                  tabIndex={0}
                >
                  🛡️ Admin Panel
                </button>
              )}
              <button
                className="dropdown-item"
                onClick={handleLogoutClick}
                onKeyDown={(e) => handleKeyDown(e, handleLogoutClick)}
                role="menuitem"
                tabIndex={0}
              >
                🚪 Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardBar;
